


import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCalendar, FiClock, FiUser, FiBookOpen, FiArrowRight } from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api.js';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await api.get('/api/posts');
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Blog - Portfolio</title>
        <meta name="description" content="Read my latest blog posts about web development and technology" />
      </Helmet>

      <div className="pt-20 section-padding bg-dark-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-center mb-6 text-gray-900 dark:text-gray-100">
              Blog
            </h1>
            <p className="text-xl text-center mb-12 max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
              Thoughts on web development, technology trends, and lessons learned along the way.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <BlogCard key={post._id} post={post} index={index} />
              ))}
            </div>

            {posts.length === 0 && (
              <div className="text-center py-12">
                <p className="text-xl text-gray-600 dark:text-gray-400">
                  No blog posts found. Check back soon for new content!
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
};

const BlogCard = ({ post, index }) => {
  const backendUrl = import.meta.env.VITE_API_URL;
  const imageUrl = post.image
    ? post.image.startsWith('http')
      ? post.image
      : `${backendUrl}${post.image}`
    : 'https://via.placeholder.com/400x225';

  return (
    <motion.article
      className="glass-effect rounded-2xl p-6 shadow-2xl border border-white/5 dark:border-white/10 transition-all duration-700 hover:shadow-secondary_accent/10 hover:-translate-y-3 overflow-hidden relative group"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: "easeOut" }}
    >
      {/* Animated background glow */}
      <div className="absolute -inset-0.5 bg-gradient-to-r from-secondary_accent/20 to-accent/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>

      {/* Background Icon Decor */}
      <div className="absolute -right-6 -bottom-6 opacity-[0.05] group-hover:opacity-[0.1] transition-all duration-700 group-hover:scale-125 group-hover:-rotate-12 pointer-events-none z-0">
        <FiBookOpen size={180} />
      </div>

      <div className="aspect-video mb-6 rounded-xl overflow-hidden relative z-10 border border-white/5">
        <img
          src={imageUrl}
          alt={post.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute top-4 right-4 z-20">
          <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-dark-900/60 backdrop-blur-md text-white border border-white/10">
            {post.category || 'Article'}
          </span>
        </div>
      </div>
      
      <div className="relative z-10 flex flex-col h-full">
        <div className="flex items-center gap-4 mb-4 text-[10px] font-medium uppercase tracking-widest text-gray-500">
          <div className="flex items-center gap-1.5 group-hover:text-gray-300 transition-colors">
            <FiCalendar size={12} className="text-secondary_accent" />
            <span>{new Date(post.createdAt).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-1.5 group-hover:text-gray-300 transition-colors">
            <FiClock size={12} className="text-secondary_accent" />
            <span>{post.readTime || '5 min read'}</span>
          </div>
        </div>
        
        <h2 className="text-2xl font-bold mb-3 text-white group-hover:text-secondary_accent transition-colors tracking-tight leading-tight">
          <Link to={`/blog/${post.slug}`}>
            {post.title}
          </Link>
        </h2>
        
        <p className="text-gray-400 mb-6 line-clamp-2 text-sm leading-relaxed font-light">
          {post.excerpt}
        </p>
        
        <div className="mt-auto flex items-center justify-between pt-4 border-t border-white/5">
          <div className="flex flex-wrap gap-2">
            {post.tags?.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="text-[10px] uppercase tracking-tighter font-bold text-gray-500"
              >
                #{tag}
              </span>
            ))}
          </div>
          
          <Link
            to={`/blog/${post.slug}`}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-secondary_accent hover:text-white transition-all duration-300 group/link"
          >
            Read More
            <FiArrowRight className="group-hover/link:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

export default Blog;